/* eslint-disable import/no-cycle */
/* eslint-disable max-lines */
import { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import {
  Map,
  useRouter,
  useMediaQuery,
  useI18n,
  useAsync,
} from '@popety_io/popety-io-lib'

import mapboxgl from 'mapbox-gl'
import { useTheme } from '@mui/material'
import listingHistoryService from '../../../redux/summary/summaryService'
import { comparablesIdsSelector } from '../../../redux'
import EstimateListingPopup from '../../../../../components/EstimateListingPopup'
import { getListingsTiles, removeListingTiles } from './listing.layers'

const fillStyle = 'layer_fill_listing'

const EstimateListing = ({ map, loaded }: { map: Map; loaded?: boolean }) => {
  const { dispatch, data, loading } = useAsync<any[]>()
  const { t } = useI18n()
  const router = useRouter()
  const comparablesIds = useSelector(comparablesIdsSelector)

  const [feature, setFeature] = useState<any>()
  const [coordinatesCurrent, setCoordinatesCurrent] = useState<any>()
  const [popup, setPopup] = useState<mapboxgl.Popup>()
  const [popupListing, setPopupListing] = useState<mapboxgl.Popup>()
  const touchScreen = useMediaQuery('(hover: none)')
  const theme = useTheme()

  const color = theme?.palette?.primary?.main || '#00ade6'

  const isListingTab =
    router.query.tab === 'listing' || router.query.subTab === 'listing'

  const filters = {
    comparablesIds: comparablesIds?.length
      ? comparablesIds?.map((id: number) => id?.toString())
      : [0],
  }

  const query = filters && encodeURIComponent(JSON.stringify(filters))

  const { listingCenter, title } = router.query

  const containerRef = useRef<any>(null)

  const handleClose = () => {
    setFeature(null)
  }

  const handleClickItemPopup = () => {
    if (map && popupListing) {
      popupListing.remove()
    }
  }

  const handleClickMap = () => {
    if (map && popupListing) {
      popupListing.remove()
    }
  }

  const handleClick = (e: any) => {
    if (map && popupListing) {
      popupListing.remove()
    }

    dispatch(() =>
      listingHistoryService.getListingTileDetails(
        e.features.map(
          (f: { properties: { _key: string } }) => f.properties._key,
        ),
        query,
      ),
    )

    const coordinates = e.features[0].geometry.coordinates.slice()

    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
    }

    setCoordinatesCurrent(coordinates)
  }

  const handleMouseMove = (e: any) => {
    map.getCanvas().style.cursor = 'pointer'
    if (popup) {
      const coordinates = e.features[0].geometry.coordinates.slice()
      const str =
        e.features[0].properties._count > 1
          ? `${t('listing.listing')}s`
          : t('listing.listing')
      const description = `${e.features[0].properties._count} ${str}`

      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
      }

      popup.setLngLat(coordinates).setHTML(description).addTo(map)
    }
  }

  const handleMouseLeave = () => {
    map.getCanvas().style.cursor = ''

    if (popup) {
      popup.remove()
    }
  }

  const addTransactionTiles = () => {
    try {
      getListingsTiles({
        map,
        query,
        color,
      })
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    setPopup(
      new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      }),
    )
    setPopupListing(
      new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'custom-popup-listing',
      }),
    )
  }, [])

  useEffect(() => {
    if (!isListingTab && map && loaded) {
      removeListingTiles({ map })
    }
  }, [isListingTab, map, loaded])

  useEffect(() => {
    if (touchScreen) setPopup(undefined)
  }, [touchScreen])

  useEffect(() => {
    if (!map || !loaded || !isListingTab) return

    addTransactionTiles()

    map.on('style.load', addTransactionTiles)
    map.on('click', fillStyle, handleClick)
    map.on('mousemove', fillStyle, handleMouseMove)
    map.on('mouseleave', fillStyle, handleMouseLeave)

    map.on('click', handleClickMap)

    return () => {
      map.off('style.load', addTransactionTiles)
      map.off('click', fillStyle, handleClick)
      map.off('mousemove', fillStyle, handleMouseMove)
      map.off('mouseleave', fillStyle, handleMouseLeave)
      map.off('click', handleClickMap)
    }
  }, [isListingTab, loaded, query, listingCenter, title])

  useEffect(() => {
    if (data?.length) {
      setFeature(data[0])
    }
  }, [data])

  useEffect(() => {
    if (loading && popupListing) {
      popupListing.remove()
    }

    if (!isListingTab && popupListing) {
      popupListing.remove()
      setCoordinatesCurrent(null)
    }

    if (
      !loading &&
      data &&
      coordinatesCurrent &&
      popupListing &&
      containerRef.current &&
      isListingTab &&
      map
    ) {
      if (data?.length === 0) {
        popupListing.remove()

        return
      }
      if (data?.length === 1) {
        router.updateQuery({
          listingId: data?.[0]?._id || data?.[0]?.id || null,
        })
      } else {
        popupListing
          .setLngLat(coordinatesCurrent)
          .setDOMContent(containerRef.current)
          .addTo(map)
      }
    }
  }, [
    data,
    loading,
    popupListing,
    coordinatesCurrent,
    router.query?.tab,
    map,
    isListingTab,
  ])

  useEffect(() => {
    if (map && loaded && listingCenter && popup && title) {
      const [lng, lat] = listingCenter.split(',')

      map.flyTo({ center: [lng, lat], zoom: 17 })
      popup.setLngLat([lng, lat]).setHTML(title).addTo(map)
    } else if (map && loaded && !listingCenter && popup && !title) {
      popup.remove()
    }
  }, [listingCenter, map, loaded, title, popup])

  return (
    <EstimateListingPopup
      data={data}
      feature={feature}
      handleClose={handleClose}
      ref={containerRef}
      loading={loading}
      onClick={handleClickItemPopup}
    />
  )
}

export default EstimateListing
